import { type BoxProps, type MantineSize } from "@mantine/core";
import { getRadius } from "@mantine/core";
import cx from "clsx";
import { type FC } from "react";
import videojs from "video.js";
import type Player from "video.js/dist/types/player";

import classes from "./VideoJS.module.css";
import "video.js/dist/video-js.css";

export interface VideoJSProps extends BoxProps {
  options?: Record<string, any>;
  radius?: MantineSize;
  onReady?: (player: Player) => void;
}

const VideoJS: FC<VideoJSProps> = ({
  options,
  radius = "default",
  className,
  style,
  onReady,
  ...otherProps
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      invariant(containerRef.current);

      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoEl = document.createElement("video-js");
      videoEl.classList.add("vjs-big-play-centered");
      containerRef.current.appendChild(videoEl);

      const player = videojs(videoEl, options, () => {
        onReady?.(player);
      });
      playerRef.current = player;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dispose the Video.js player when the functional component unmouGnts
  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      mod={{ "vjs-player": true }}
      className={cx(classes.container, className)}
      style={[style, { "--vjs-radius": getRadius(radius) }]}
      {...otherProps}
    />
  );
};

export default VideoJS;
